<template>
  <v-card class="packlist-events-card" elevation="4">
    <v-card-title>
      <h2 class="section-title">
        <v-icon left>mdi-package-variant</v-icon>
        Packlisten Aktivität
      </h2>
      <v-spacer></v-spacer>
      <v-chip color="info" small>
        {{ activePackersFiltered.length }} Aktiv
      </v-chip>
    </v-card-title>
    <v-card-text>
      <!-- Active Packers -->
      <div v-if="activePackersFiltered.length > 0" class="active-packers mb-4">
        <h3 class="metric-title mb-2">Aktive Packer</h3>
        <v-row>
          <v-col 
            v-for="packer in activePackersFiltered" 
            :key="packer.userId"
            cols="6" md="3"
          >
            <v-card 
              class="packer-card"
              :class="{ 'packing-animation': packer.isPacking && !packer.isStale }"
              elevation="2"
            >
              <v-card-text class="d-flex align-center">
                <v-avatar size="40" :color="packer.isStale ? 'warning' : (packer.isPacking ? 'success' : 'primary')">
                  <v-icon dark>{{ packer.isStale ? 'mdi-clock-alert' : 'mdi-account' }}</v-icon>
                </v-avatar>
                <div class="ml-3 flex-grow-1">
                  <div class="packer-name">{{ packer.username }}</div>
                  <div class="packer-status">
                    <v-icon small :color="packer.isStale ? 'warning' : (packer.isPacking ? 'success' : 'primary')">
                      {{ packer.isPacking ? 'mdi-package-variant-closed' : 'mdi-package-variant' }}
                    </v-icon>
                    {{ packer.currentOrder 
                        ? (packer.currentOrder.length > 12 
                            ? packer.currentOrder.slice(0, 12) + '…' 
                            : packer.currentOrder) 
                        : 'Bereit' }}
                  </div>
                  <div v-if="packer.startTime" class="packer-timer" :class="{ 'text-warning': packer.isStale }">
                    <v-icon x-small>mdi-timer</v-icon>
                    {{ formatPackingTime(packer.startTime) }}
                  </div>
                </div>
                <div v-if="packer.isPacking && !packer.isStale" class="packing-indicator">
                  <v-progress-circular
                    indeterminate
                    size="24"
                    width="2"
                    color="success"
                  ></v-progress-circular>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
      
      <!-- Recent Packed Orders -->
      <div class="recent-packed">
        <h3 class="metric-title mb-2">Zuletzt gepackte Bestellungen</h3>
        <div class="packed-orders-scroll">
          <v-list dense dark class="packed-orders-list pa-0">
            <transition-group name="pack-list" tag="div">
              <v-list-item
                v-for="(pack, index) in recentPackedOrders"
                :key="pack.id"
                class="packed-order-item"
                :class="{ 'pack-complete-animation': index === 0 && pack.isNew, 'new-item': index === 0 && pack.isNew }"
                two-line
              >
                <v-list-item-avatar size="28">
                  <v-icon
                    :color="pack.isMultiCart ? 'purple' : (index === 0 && pack.isNew ? 'success' : 'grey')"
                    small
                  >
                    {{ pack.isMultiCart ? 'mdi-cart' : 'mdi-package-variant' }}
                  </v-icon>
                </v-list-item-avatar>
                
                <v-list-item-content class="py-1">
                  <v-list-item-title class="packed-order-title d-flex align-center">
                    <span class="font-weight-bold mr-2">{{ pack.orderId || 'N/A' }}</span>
                    <span class="caption grey--text">{{ pack.username }}</span>
                    <v-spacer></v-spacer>
                    <v-chip v-if="pack.packingTime" x-small color="primary" class="ml-2">
                      <v-icon left x-small>mdi-timer</v-icon>
                      {{ formatDuration(pack.packingTime) }}
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle class="packed-order-subtitle">
                    <span>{{ formatDetailedTime(pack.timestamp) }}</span>
                    <span v-if="pack.isMultiCart" class="ml-3">
                      <v-icon x-small>mdi-package-variant</v-icon>
                      {{ pack.itemCount }} Artikel → Regal {{ pack.location }}
                    </span>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </transition-group>
          </v-list>
        </div>
      </div>
      
      <!-- Statistics -->
      <v-card flat class="mt-4 pa-3" color="rgba(0, 0, 0, 0.2)">
        <v-row>
          <v-col cols="4" class="text-center">
            <div class="stat-value">{{ stats.todayTotal }}</div>
            <div class="stat-label">Heute gepackt</div>
          </v-col>
          <v-col cols="4" class="text-center">
            <div class="stat-value">{{ stats.avgTime }}</div>
            <div class="stat-label">Ø Zeit</div>
          </v-col>
          <v-col cols="4" class="text-center">
            <div class="stat-value">{{ stats.activeNow }}</div>
            <div class="stat-label">In Bearbeitung</div>
          </v-col>
        </v-row>
        
        <!-- Cache Info and Load More -->
        <v-divider class="my-3"></v-divider>
        <div class="text-center">
          <div class="cache-info mb-2">
            <v-icon small>mdi-database</v-icon>
            {{ cacheInfo.loadedPacklistEvents }} von {{ cacheInfo.totalPacklistEvents }} Events geladen
          </div>
          <v-btn
            v-if="cacheInfo.canLoadMore"
            small
            color="primary"
            :loading="loadingMoreEvents"
            @click="$emit('load-more')"
          >
            <v-icon left small>mdi-plus</v-icon>
            Weitere 500 laden
          </v-btn>
        </div>
      </v-card>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from 'moment';

export default {
  name: 'PacklistActivity',
  
  props: {
    activePackers: {
      type: Array,
      default: () => []
    },
    recentPackedOrders: {
      type: Array,
      default: () => []
    },
    stats: {
      type: Object,
      default: () => ({
        todayTotal: 0,
        avgTime: '00:00',
        activeNow: 0
      })
    },
    cacheInfo: {
      type: Object,
      default: () => ({
        loadedPacklistEvents: 0,
        totalPacklistEvents: 0,
        canLoadMore: false
      })
    },
    loadingMoreEvents: {
      type: Boolean,
      default: false
    }
  },
  
  computed: {
    activePackersFiltered() {
      return this.activePackers.filter(packer => {
        if (!packer.isPacking || !packer.startTime) return false;
        const elapsed = Date.now() - new Date(packer.startTime).getTime();
        const threeMinutes = 3 * 60 * 1000;
        return elapsed <= threeMinutes;
      });
    }
  },
  
  methods: {
    formatPackingTime(startTime) {
      if (!startTime) return '00:00';
      const start = new Date(startTime);
      const now = new Date();
      const diff = Math.floor((now - start) / 1000);
      const minutes = Math.floor(diff / 60);
      const seconds = diff % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    },
    
    formatEventTime(timestamp) {
      if (!timestamp) return '';
      return moment(timestamp).format('HH:mm');
    },
    
    formatDetailedTime(timestamp) {
      if (!timestamp) return '';
      return moment(timestamp).format('DD.MM HH:mm:ss');
    },
    
    formatDuration(ms) {
      const totalSeconds = Math.floor(ms / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },
    
    getTimelineColor(pack, index) {
      if (pack.isMultiCart) return 'purple';
      if (index === 0 && pack.isNew) return 'success';
      if (index < 5) return 'primary';
      return 'grey';
    },
    
    getCardColor(pack, index) {
      if (pack.isMultiCart) return 'rgba(156, 39, 176, 0.1)';
      if (index === 0 && pack.isNew) return 'rgba(76, 175, 80, 0.15)';
      return 'rgba(0, 0, 0, 0.3)';
    }
  },
  
  mounted() {
    // Start timer update interval
    this.timerInterval = setInterval(() => {
      this.$forceUpdate(); // Force update to refresh timers
    }, 1000);
  },
  
  beforeDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }
};
</script>

<style scoped>
.section-title {
  font-size: 1.2rem;
  font-weight: 500;
}

.packlist-events-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%);
}

.metric-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.packer-card {
  background: rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}

.packer-card:hover {
  background: rgba(255, 255, 255, 0.08);
}

.packing-animation {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

.packer-name {
  font-weight: bold;
  font-size: 0.95rem;
}

.packer-status {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
}

.packer-timer {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  font-family: monospace;
}

.packed-orders-scroll {
  max-height: 300px;
  overflow-y: auto;
  padding-right: 4px;
}

.packed-orders-list {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.packed-order-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  min-height: 48px !important;
  padding: 4px 8px !important;
}

.packed-order-item:last-child {
  border-bottom: none;
}

.packed-order-item.new-item {
  background: rgba(76, 175, 80, 0.1);
  animation: highlight 1s ease-out;
}

@keyframes highlight {
  0% {
    background: rgba(76, 175, 80, 0.3);
  }
  100% {
    background: rgba(76, 175, 80, 0.1);
  }
}

.packed-order-title {
  font-size: 0.875rem !important;
  line-height: 1.2 !important;
}

.packed-order-subtitle {
  font-size: 0.75rem !important;
  color: rgba(255, 255, 255, 0.6) !important;
}

.pack-list-enter-active,
.pack-list-leave-active {
  transition: all 0.5s;
}

.pack-list-enter,
.pack-list-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.pack-complete-animation {
  animation: slideInScale 0.5s ease-out;
}

@keyframes slideInScale {
  0% {
    transform: translateX(-100px) scale(0.8);
    opacity: 0;
  }
  100% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
}

.package-success-icon {
  animation: checkBounce 0.6s ease-out;
}

@keyframes checkBounce {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

.event-time {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
}

.pack-user {
  font-weight: bold;
  font-size: 0.9rem;
}

.pack-order {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
}

.pack-timestamp,
.pack-time,
.pack-multicart {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 2px;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #fff;
}

.stat-label {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 4px;
}

.cache-info {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
}
</style>